import { createSlice } from "@reduxjs/toolkit";

import { ChatState } from "@/types/store";
import {
  createChatConversation,
  getChatConversations,
  loadMoreChatConversations,
  getArchivedConversations,
  loadMoreArchivedConversations,
  archiveConversation,
  unarchiveConversation,
  getChatMessages,
  getUnreadMessagesCount,
  deleteConversation,
  getChatConversationAfterRemoval,
} from "@/api/chat/thunks";

const initialState: ChatState = {
  conversations: [],
  archivedConversations: [],
  selectedConversation: null,
  messages: {},
  unreadCount: 0,
  loading: false,
  loadingMore: false,
  loadingMessages: false,
  archivedLoading: false,
  archivedLoadingMore: false,
  filterStartDate: null,
  filterEndDate: null,
  filterHomeowners: [],
  error: null,
  total: 0,
  hasNextPage: false,
  currentPage: 1,
  archivedTotal: 0,
  archivedHasNextPage: false,
  archivedCurrentPage: 1,
  typingUsers: {},
};

export const chatSlice = createSlice({
  name: "ChatSlice",
  initialState,
  reducers: {
    setSelectedConversation: (state, { payload }) => {
      state.selectedConversation = payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    addMessage: (state, { payload }) => {
      const { conversationId, message } = payload;
      if (!state.messages[conversationId]) {
        state.messages[conversationId] = [];
      }
      state.messages[conversationId].unshift(message);
    },
    // Rolls back an optimistic message the server rejected, so a failed send
    // does not leave a bubble that looks delivered.
    removeMessage: (state, { payload }) => {
      const { conversationId, messageId } = payload;

      if (!state.messages[conversationId]) {
        return;
      }

      state.messages[conversationId] = state.messages[conversationId].filter(
        (message) => message.id !== messageId
      );
    },
    updateUnreadCounts: (state, { payload }) => {
      const { conversationId, totalUnread, conversationUnread } = payload;

      // Update total unread count
      state.unreadCount = totalUnread;

      // Update specific conversation unread count
      const conversation = state.conversations.find(
        (c) => c.id === conversationId
      );
      if (conversation) {
        conversation.unreadMessagesCount = conversationUnread;
      }

      // Also check archived conversations
      const archivedConversation = state.archivedConversations.find(
        (c) => c.id === conversationId
      );
      if (archivedConversation) {
        archivedConversation.unreadMessagesCount = conversationUnread;
      }
    },
    updateConversationLastMessage: (state, { payload }) => {
      const { conversationId, lastMessage, timestamp } = payload;

      // Update conversation in regular conversations
      const conversationIndex = state.conversations.findIndex(
        (c) => c.id === conversationId
      );
      if (conversationIndex !== -1) {
        const conversation = state.conversations[conversationIndex];
        conversation.lastMessage = lastMessage;
        conversation.timestamp = timestamp;

        // Move updated conversation to the top of the list for immediate visual feedback
        if (conversationIndex > 0) {
          state.conversations.splice(conversationIndex, 1);
          state.conversations.unshift(conversation);
        }
      }

      // Check archived conversations
      const archivedConversationIndex = state.archivedConversations.findIndex(
        (c) => c.id === conversationId
      );
      if (archivedConversationIndex !== -1) {
        const archivedConversation =
          state.archivedConversations[archivedConversationIndex];
        archivedConversation.lastMessage = lastMessage;
        archivedConversation.timestamp = timestamp;

        // Move updated conversation to the top of archived list too
        if (archivedConversationIndex > 0) {
          state.archivedConversations.splice(archivedConversationIndex, 1);
          state.archivedConversations.unshift(archivedConversation);
        }
      }
    },
    markConversationAsRead: (state, { payload }) => {
      const { conversationId } = payload;

      // Find and update conversation in regular conversations
      const conversation = state.conversations.find(
        (c) => c.id === conversationId
      );
      if (conversation && conversation.unreadMessagesCount > 0) {
        state.unreadCount = Math.max(
          0,
          state.unreadCount - conversation.unreadMessagesCount
        );
        conversation.unreadMessagesCount = 0;
      }

      // Also check archived conversations
      const archivedConversation = state.archivedConversations.find(
        (c) => c.id === conversationId
      );
      if (
        archivedConversation &&
        archivedConversation.unreadMessagesCount > 0
      ) {
        state.unreadCount = Math.max(
          0,
          state.unreadCount - archivedConversation.unreadMessagesCount
        );
        archivedConversation.unreadMessagesCount = 0;
      }
    },
    setUserTyping: (state, { payload }) => {
      const { conversationId, user } = payload;

      if (!state.typingUsers[conversationId]) {
        state.typingUsers[conversationId] = [];
      }

      const existingUser = state.typingUsers[conversationId].find(
        (u) => u.userId === user.userId
      );

      if (!existingUser) {
        state.typingUsers[conversationId].push(user);
      }
    },
    setUserStoppedTyping: (state, { payload }) => {
      const { conversationId, userId } = payload;

      if (state.typingUsers[conversationId]) {
        state.typingUsers[conversationId] = state.typingUsers[
          conversationId
        ].filter((u) => u.userId !== userId);
      }
    },
    clearTypingUsers: (state, { payload }) => {
      const { conversationId } = payload;
      if (state.typingUsers[conversationId]) {
        state.typingUsers[conversationId] = [];
      }
    },
    setFilterStartDate: (state, { payload }) => {
      state.filterStartDate = payload;
    },
    setFilterEndDate: (state, { payload }) => {
      state.filterEndDate = payload;
    },
    setFilterHomeowners: (state, { payload }) => {
      state.filterHomeowners = payload;
    },
    addNewConversation: (state, { payload }) => {
      // Add the new conversation to the beginning of the list
      const newConversation = payload;
      const existingIndex = state.conversations.findIndex(
        (conv) => conv.id === newConversation.id
      );

      if (existingIndex === -1) {
        // Only add if it doesn't already exist
        state.conversations.unshift(newConversation);
        state.total += 1;
      }
    },
    setCurrentPage: (state, { payload }) => {
      state.currentPage = payload;
    },
    setArchivedCurrentPage: (state, { payload }) => {
      state.archivedCurrentPage = payload;
    },
    removeConversationFromLists: (state, { payload }) => {
      const { conversationId } = payload;
      state.conversations = state.conversations.filter(
        (conversation) => conversation.id !== conversationId
      );
      state.archivedConversations = state.archivedConversations.filter(
        (conversation) => conversation.id !== conversationId
      );
      delete state.messages[conversationId];
      if (state.selectedConversation === conversationId) {
        state.selectedConversation = null;
      }
    },
    removeConversationFromActive: (state, { payload }) => {
      const { conversationId } = payload;
      state.conversations = state.conversations.filter(
        (conversation) => conversation.id !== conversationId
      );
      if (state.selectedConversation === conversationId) {
        state.selectedConversation = null;
      }
    },
    removeConversationFromArchived: (state, { payload }) => {
      const { conversationId, conversation } = payload;

      // Find and remove from archived list
      const removedConversation = state.archivedConversations.find(
        (conv) => conv.id === conversationId
      );

      state.archivedConversations = state.archivedConversations.filter(
        (conversation) => conversation.id !== conversationId
      );

      // Check if conversation already exists in active list to avoid duplicates
      const existsInActive = state.conversations.find(
        (conv) => conv.id === conversationId
      );

      if (!existsInActive) {
        // If we have the conversation from archived list, use it
        if (removedConversation) {
          state.conversations.unshift(removedConversation);
          state.total += 1;
        }
        // If we don't have it in archived but received conversation data from socket
        else if (conversation) {
          // Transform the backend conversation to frontend format
          const transformedConversation = {
            id: conversation.id,
            name:
              conversation.subjectHomeowner ||
              conversation.subjectAdmin ||
              "Unknown",
            avatar: conversation.homeownerProfilePicture || null,
            lastMessage: conversation.lastMessage?.content || "No messages yet",
            timestamp:
              conversation.lastMessage?.createdAt ||
              conversation.updatedAt ||
              conversation.createdAt,
            unreadCount: 0,
            unreadMessagesCount: 0,
            isRead: true,
            participantId: conversation.userId,
            userId: conversation.userId,
          };

          state.conversations.unshift(transformedConversation);
          state.total += 1;
        }
      }

      if (state.selectedConversation === conversationId) {
        state.selectedConversation = null;
      }
    },
    clearChatData: (state) => {
      Object.assign(state, initialState);
    },
    setConversations: (state, { payload }) => {
      state.conversations = payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getChatConversations.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getChatConversations.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.conversations = payload.conversations || [];
        state.total = payload.total || 0;
        state.hasNextPage = payload.hasNextPage || false;
        state.currentPage = payload.page || 1;
      })
      .addCase(getChatConversations.rejected, (state, { payload }) => {
        state.loading = false;
        state.error = payload as string;
      })
      .addCase(
        getChatConversationAfterRemoval.fulfilled,
        (state, { payload }) => {
          state.loading = false;
          if (payload.conversationType === "inbox") {
            state.conversations = [
              ...(state.conversations || []),
              payload.conversation,
            ];
          } else {
            state.archivedConversations = [
              ...(state.archivedConversations || []),
              payload.conversation,
            ];
          }
          state.total = payload.total || 0;
          state.hasNextPage = payload.hasNextPage || false;
          state.currentPage = payload.page || 1;
        }
      )
      .addCase(createChatConversation.pending, (state) => {
        // Don't set loading to true to avoid interfering with the chat list display
        state.error = null;
      })
      .addCase(createChatConversation.fulfilled, (state, { payload }) => {
        if (payload?.conversationId) {
          // Select the newly created conversation (the list will be updated by the getChatConversations call)
          state.selectedConversation = payload.conversationId;
        }
      })
      .addCase(createChatConversation.rejected, (state, { payload }) => {
        state.loading = false;
        state.error = payload as string;
      })
      .addCase(loadMoreChatConversations.pending, (state) => {
        state.loadingMore = true;
        state.error = null;
      })
      .addCase(loadMoreChatConversations.fulfilled, (state, { payload }) => {
        state.loadingMore = false;
        state.conversations = [
          ...state.conversations,
          ...(payload.conversations || []),
        ];
        state.total = payload.total || 0;
        state.hasNextPage = payload.hasNextPage || false;
        state.currentPage = payload.page || state.currentPage + 1;
      })
      .addCase(loadMoreChatConversations.rejected, (state, { payload }) => {
        state.loadingMore = false;
        state.error = payload as string;
      })
      .addCase(getChatMessages.pending, (state) => {
        state.loadingMessages = true;
        state.error = null;
      })
      .addCase(getChatMessages.fulfilled, (state, { payload }) => {
        state.loadingMessages = false;
        if (payload) {
          state.messages[payload.conversationId] = payload.messages;
        }
      })
      .addCase(getChatMessages.rejected, (state, { payload }) => {
        state.loadingMessages = false;
        state.error = payload as string;
      })
      .addCase(getArchivedConversations.pending, (state) => {
        state.archivedLoading = true;
        state.error = null;
      })
      .addCase(getArchivedConversations.fulfilled, (state, { payload }) => {
        state.archivedLoading = false;
        state.archivedConversations = payload.conversations || [];
        state.archivedTotal = payload.total || 0;
        state.archivedHasNextPage = payload.hasNextPage || false;
        state.archivedCurrentPage = payload.page || 1;
      })
      .addCase(getArchivedConversations.rejected, (state, { payload }) => {
        state.archivedLoading = false;
        state.error = payload as string;
      })
      .addCase(loadMoreArchivedConversations.pending, (state) => {
        state.archivedLoadingMore = true;
        state.error = null;
      })
      .addCase(
        loadMoreArchivedConversations.fulfilled,
        (state, { payload }) => {
          state.archivedLoadingMore = false;
          state.archivedConversations = [
            ...state.archivedConversations,
            ...(payload.conversations || []),
          ];
          state.archivedTotal = payload.total || 0;
          state.archivedHasNextPage = payload.hasNextPage || false;
          state.archivedCurrentPage =
            payload.page || state.archivedCurrentPage + 1;
        }
      )
      .addCase(loadMoreArchivedConversations.rejected, (state, { payload }) => {
        state.archivedLoadingMore = false;
        state.error = payload as string;
      })
      .addCase(archiveConversation.fulfilled, (state, { payload }) => {
        // Remove conversation from regular conversations list
        state.conversations = state.conversations.filter(
          (conversation) => conversation.id !== payload.conversationId
        );
        // Clear selection if the archived conversation was selected
        if (state.selectedConversation === payload.conversationId) {
          state.selectedConversation = null;
        }
      })
      .addCase(archiveConversation.rejected, (state, { payload }) => {
        state.error = payload as string;
      })
      .addCase(unarchiveConversation.fulfilled, (state, { payload }) => {
        // Remove conversation from archived conversations list
        state.archivedConversations = state.archivedConversations.filter(
          (conversation) => conversation.id !== payload.conversationId
        );
        // Clear selection if the unarchived conversation was selected
        if (state.selectedConversation === payload.conversationId) {
          state.selectedConversation = null;
        }
      })
      .addCase(unarchiveConversation.rejected, (state, { payload }) => {
        state.error = payload as string;
      })
      .addCase(deleteConversation.fulfilled, (state, { payload }) => {
        // Remove conversation from both regular and archived conversations
        state.conversations = state.conversations.filter(
          (conversation) => conversation.id !== payload.conversationId
        );
        state.archivedConversations = state.archivedConversations.filter(
          (conversation) => conversation.id !== payload.conversationId
        );
        // Clear messages for this conversation
        delete state.messages[payload.conversationId];
        // Clear selection if the deleted conversation was selected
        if (state.selectedConversation === payload.conversationId) {
          state.selectedConversation = null;
        }
      })
      .addCase(deleteConversation.rejected, (state, { payload }) => {
        state.error = payload as string;
      })
      .addCase(getUnreadMessagesCount.fulfilled, (state, { payload }) => {
        state.unreadCount = payload;
      })
      .addCase(getUnreadMessagesCount.rejected, (state, { payload }) => {
        state.error = payload as string;
      });
  },
});

export const {
  setSelectedConversation,
  clearError,
  addMessage,
  removeMessage,
  updateUnreadCounts,
  updateConversationLastMessage,
  markConversationAsRead,
  setUserTyping,
  setUserStoppedTyping,
  clearTypingUsers,
  setFilterStartDate,
  setFilterEndDate,
  setFilterHomeowners,
  addNewConversation,
  setCurrentPage,
  setArchivedCurrentPage,
  removeConversationFromLists,
  removeConversationFromActive,
  removeConversationFromArchived,
  clearChatData,
  setConversations,
} = chatSlice.actions;

export default chatSlice.reducer;
